/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";

import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";
import BasicCrudScreenActionItemLayout from "../screenTypes/layout/BasicCrudScreenActionItemLayout";
import getSchema from "../../lib/api/getSchema";
import { useSetRecoilState } from "recoil";
import notificationState from "../../states/notificationState";
import DeleteRecordButton from "../input/DeleteRecordButton";
import CreateRecordButton from "../input/CreateRecordButton";
import UpdateRecordButton from "../input/UpdateRecordButton";

/**
 * @COMPONENT_NAME BarcodeGenerateRule
 * @DESCRIPTION 바코드 생성 규칙을 관리하는 화면 컴포넌트
 *
 * @PASSTHROUGH_PROPS
 *   - {Object} api - 바코드 생성 규칙 API 객체
 *   - {Object} grid - 바코드 생성 규칙 그리드 객체 참조
 *   - {Object} selectedGrid - 선택된 바코드 생성 규칙 정보
 *
 * @CALLBACKS
 *   - {Function} handleResize - 화면 크기 변경 시 호출되는 콜백
 *   - {Function} onApiInitialized - API 초기화 콜백-
 *   - {Function} onInitialized - 그리드 초기화 콜백
 *  */

const BarcodeGenerateRule = () => {
    const table = "BarcodeGenerateRule";

    const [schema, setSchema] = useState();
    const [api, setApi] = useState();
    const [grid, setGrid] = useState();
    const [selectedGrid, setSelectedGrid] = useState();
    const setNotification = useSetRecoilState(notificationState);

    useEffect(() => {
        let mounted = true;

        (async () => {
            const schema = await getSchema(table);

            if (mounted) {
                setSchema(schema);
            }
        })();

        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        if (grid) {
            const handleGridSelection = (grid, event) => {
                setSelectedGrid(grid.selectedItems?.[0]);
            };
            grid.selectionChanged.addHandler(handleGridSelection);
            grid.itemsSourceChanged.addHandler(handleGridSelection);
            return () => {
                if (grid && !grid.isDestroyed) {
                    grid.selectionChanged.removeHandler(handleGridSelection);
                    grid.itemsSourceChanged.removeHandler(handleGridSelection);
                }
            };
        }
    }, [grid]);

    const onApiInitialized = (api) => {
        setApi(api);
    };

    const onInitialized = (grid) => {
        setGrid(grid);
    };

    const notifySuccess = (msg) => {
        setNotification({
            severity: "success",
            msg: msg,
            open: true,
        });
    };

    const notifyFailure = (msg) => {
        setNotification({
            severity: "error",
            msg: msg,
            open: true,
        });
    };

    const notifyWarning = (msg) => {
        setNotification({
            severity: "warning",
            msg: msg,
            open: true,
        });
    };

    const onSuccess = (msg, position) => {
        notifySuccess(msg);
        api?.refresh();
    };

    const onFailure = (reason) => {
        notifyFailure(reason);
    };

    const onNotifyWarning = (msg) => {
        notifyWarning(msg);
        api?.refresh();
    };

    return (
        <>
            <BasicCrudScreenType
                id={table}
                table={table}
                view={table}
                onInitialized={onInitialized}
                onApiInitialized={onApiInitialized}
                noCreate
                noEdit
                noDelete
                noExcel
                onAppendButtonInFrontOfSearch={() => (
                    <>
                        <BasicCrudScreenActionItemLayout>
                            <CreateRecordButton
                                id={table}
                                schema={schema}
                                refresh={api}
                                onSuccess={onSuccess}
                                onFailure={onFailure}
                            />
                        </BasicCrudScreenActionItemLayout>
                        <BasicCrudScreenActionItemLayout>
                            <UpdateRecordButton
                                id={table}
                                grid={grid}
                                schema={schema}
                                refresh={api}
                                disabled={!selectedGrid}
                                onSuccess={onSuccess}
                                onFailure={onFailure}
                            />
                        </BasicCrudScreenActionItemLayout>
                        <BasicCrudScreenActionItemLayout>
                            <DeleteRecordButton
                                id={table}
                                schema={schema}
                                grid={grid}
                                refresh={api}
                                disabled={!selectedGrid}
                                color="secondary"
                                onSuccess={onSuccess}
                                onFailure={onFailure}
                                onPartialFailure={onNotifyWarning}
                            />
                        </BasicCrudScreenActionItemLayout>
                    </>
                )}
            />
        </>
    );
};

export default BarcodeGenerateRule;
